import { memo, type ReactNode } from 'react';

import { ListTodoIcon } from '@air/design-icons';
import { Spinner } from '@air/primitive-spinner';
import { type AgentSessionResponse } from '@air/server-sdk';
import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentPanelMyTasksFooter } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksFooter';
import { WorkspaceAgentPanelMyTasksSectionHeader } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksSectionHeader';
import {
  WORKSPACE_AGENT_MY_TASKS_COMPACT_ROW_HEIGHT,
  WORKSPACE_AGENT_MY_TASKS_ROW_GAP,
  WORKSPACE_AGENT_MY_TASKS_ROW_HEIGHT,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentMyTasksRowLayout';
import { useWorkspaceAgentMyTasksVirtualList } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentMyTasksVirtualList';
import {
  isWorkspaceAgentMyTasksItemRow,
  type WorkspaceAgentOnboardingReviewTaskPlacement,
  type WorkspaceAgentSessionVirtualRow,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentSessionVirtualRows';
import { type WorkspaceAgentSessionBucket } from '~/components/v2/components/workspace/workspace-agent-panel/utils/classifyWorkspaceAgentSessionBucket';
import { type Automation } from '~/hooks/queries/automations/usePrivateAutomationsList';

export type WorkspaceAgentPanelMyTasksSessionRowRenderProps = {
  bucket: WorkspaceAgentSessionBucket;
  session: AgentSessionResponse;
};

export type WorkspaceAgentPanelMyTasksAutomationRowRenderProps = {
  automation: Automation;
};

export type WorkspaceAgentPanelMyTasksOnboardingReviewTask = {
  placement: WorkspaceAgentOnboardingReviewTaskPlacement;
  render: () => ReactNode;
};

export type WorkspaceAgentPanelMyTasksListProps = {
  automations: Automation[];
  isLoadingAutomations: boolean;
  sessions: AgentSessionResponse[];
  isLoadingSessions: boolean;
  showAutomationsSection: boolean;
  fetchNextAutomationsPage: () => Promise<unknown>;
  fetchNextArchivedSessionsPage: () => Promise<unknown>;
  fetchNextLiveSessionsPage: () => Promise<unknown>;
  hasNextAutomationsPage: boolean;
  hasNextArchivedSessionsPage: boolean;
  hasNextLiveSessionsPage: boolean;
  isFetchingNextAutomationsPage: boolean;
  isFetchingNextArchivedSessionsPage: boolean;
  isFetchingNextLiveSessionsPage: boolean;
  focusAutomationRowId?: string | null;
  isAutomationsSectionExpanded: boolean;
  isStartNewTaskDisabled: boolean;
  onFocusAutomationRowComplete?: () => void;
  onAutomationsSectionExpandedChange: (isExpanded: boolean) => void;
  onStartNewTask: () => void;
  renderAutomationRow: (props: WorkspaceAgentPanelMyTasksAutomationRowRenderProps) => ReactNode;
  renderSessionRow: (props: WorkspaceAgentPanelMyTasksSessionRowRenderProps) => ReactNode;
  onboardingReviewTask?: WorkspaceAgentPanelMyTasksOnboardingReviewTask;
};

const getWorkspaceAgentMyTasksItemRowMinHeight = (row: WorkspaceAgentSessionVirtualRow): number | undefined => {
  if (!isWorkspaceAgentMyTasksItemRow(row)) {
    return undefined;
  }

  if (row.type === 'automation') {
    return WORKSPACE_AGENT_MY_TASKS_COMPACT_ROW_HEIGHT;
  }

  return WORKSPACE_AGENT_MY_TASKS_ROW_HEIGHT;
};

const renderVirtualRow = (
  row: WorkspaceAgentSessionVirtualRow,
  renderSessionRow: WorkspaceAgentPanelMyTasksListProps['renderSessionRow'],
  renderOnboardingReviewTask: (() => ReactNode) | undefined,
  onToggleArchivedSection: () => void,
  onToggleAutomationsSection: () => void,
  renderAutomationRow: WorkspaceAgentPanelMyTasksListProps['renderAutomationRow'],
) => {
  switch (row.type) {
    case 'section-header':
      return (
        <WorkspaceAgentPanelMyTasksSectionHeader
          count={row.count}
          isCollapsible={row.isCollapsible}
          isExpanded={row.isExpanded}
          isFirst={row.isFirst}
          label={row.label}
          onToggle={row.isCollapsible ? onToggleArchivedSection : undefined}
          section={row.bucket}
        />
      );
    case 'automations-section-header':
      return (
        <WorkspaceAgentPanelMyTasksSectionHeader
          count={row.count}
          isCollapsible={true}
          isExpanded={row.isExpanded}
          isFirst={row.isFirst}
          label="Automations"
          onToggle={onToggleAutomationsSection}
          section="automations"
        />
      );
    case 'automation':
      return renderAutomationRow({ automation: row.automation });
    case 'session':
      return renderSessionRow({ bucket: row.bucket, session: row.session });
    case 'onboarding-review':
      return renderOnboardingReviewTask?.() ?? null;
    case 'load-more':
      return (
        <output className="flex h-12 w-full items-center justify-center">
          <Spinner aria-label="Loading more tasks" className="size-4 ads:text-icon-subtle text-gray-9" role="status" />
        </output>
      );
  }
};

export const WorkspaceAgentPanelMyTasksList = memo(
  ({
    automations,
    isLoadingAutomations,
    sessions,
    isLoadingSessions,
    showAutomationsSection,
    fetchNextAutomationsPage,
    fetchNextArchivedSessionsPage,
    fetchNextLiveSessionsPage,
    hasNextAutomationsPage,
    hasNextArchivedSessionsPage,
    hasNextLiveSessionsPage,
    isFetchingNextAutomationsPage,
    isFetchingNextArchivedSessionsPage,
    isFetchingNextLiveSessionsPage,
    focusAutomationRowId,
    isAutomationsSectionExpanded,
    isStartNewTaskDisabled,
    onAutomationsSectionExpandedChange,
    onFocusAutomationRowComplete,
    onStartNewTask,
    renderAutomationRow,
    renderSessionRow,
    onboardingReviewTask,
  }: WorkspaceAgentPanelMyTasksListProps) => {
    const {
      scrollElementRef,
      toggleArchivedSection,
      toggleAutomationsSection,
      virtualRows,
      virtualizer,
      virtualItems,
    } = useWorkspaceAgentMyTasksVirtualList({
      automations,
      focusAutomationRowId,
      fetchNextAutomationsPage,
      onFocusAutomationRowComplete,
      fetchNextArchivedPage: fetchNextArchivedSessionsPage,
      fetchNextLivePage: fetchNextLiveSessionsPage,
      hasNextAutomationsPage,
      hasNextArchivedPage: hasNextArchivedSessionsPage,
      hasNextLivePage: hasNextLiveSessionsPage,
      isFetchingNextAutomationsPage,
      isFetchingNextArchivedPage: isFetchingNextArchivedSessionsPage,
      isFetchingNextLivePage: isFetchingNextLiveSessionsPage,
      sessions,
      isAutomationsSectionExpanded,
      onboardingReviewTaskPlacement: onboardingReviewTask?.placement,
      onAutomationsSectionExpandedChange,
      showAutomationsSection,
    });

    const hasOnboardingReviewTask = onboardingReviewTask !== undefined;
    const hasListContent = sessions.length > 0 || hasOnboardingReviewTask || showAutomationsSection;
    const showEmptyState =
      !isLoadingSessions &&
      !isLoadingAutomations &&
      sessions.length === 0 &&
      !hasOnboardingReviewTask &&
      !showAutomationsSection;
    const showLoadingState =
      (isLoadingSessions || isLoadingAutomations) &&
      sessions.length === 0 &&
      !hasOnboardingReviewTask &&
      !showAutomationsSection;

    return (
      <div className="flex min-h-0 flex-1 flex-col" data-testid="WORKSPACE_AGENT_PANEL_MY_TASKS">
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col',
            (showEmptyState || showLoadingState) && 'justify-center px-3 py-3',
          )}
        >
          {showLoadingState ? (
            <div className="flex items-center justify-center py-8">
              <Spinner aria-label="Loading tasks" className="size-6 ads:text-icon-subtle text-gray-9" role="status" />
            </div>
          ) : null}

          {showEmptyState ? (
            <div
              className="flex flex-col items-center px-2 text-center"
              data-testid="WORKSPACE_AGENT_PANEL_MY_TASKS_EMPTY_STATE"
            >
              <ListTodoIcon aria-hidden className="mb-3 ads:text-icon-subtle text-gray-8" size="md" />
              <p className="font-medium ads:text-primary text-14 text-gray-12">No tasks yet</p>
              <p className="mt-1.5 max-w-[280px] ads:text-metadata text-12 text-gray-9 leading-5">
                Describe a task in chat, or pick a suggestion to get started. Saved for this session.
              </p>
            </div>
          ) : null}

          {!showEmptyState && !showLoadingState && hasListContent ? (
            <div
              className="scrollbar scrollbar-track-gray-2 scrollbar-thumb-gray-7 [&:not(:hover)]:scrollbar-track-transparent min-h-0 flex-1 overflow-y-auto px-3 py-3"
              data-testid="WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_SCROLL"
              ref={scrollElementRef}
            >
              <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
                <ul
                  className="absolute top-0 left-0 m-0 w-full list-none p-0"
                  style={{ transform: `translateY(${virtualItems[0]?.start ?? 0}px)` }}
                >
                  {virtualItems.map((virtualRow) => {
                    const row = virtualRows[virtualRow.index];

                    if (!row) {
                      return null;
                    }

                    const nextRow = virtualRows[virtualRow.index + 1];
                    const hasRowGap = isWorkspaceAgentMyTasksItemRow(row) && isWorkspaceAgentMyTasksItemRow(nextRow);

                    return (
                      <li
                        className="overflow-visible"
                        data-index={virtualRow.index}
                        key={virtualRow.key}
                        ref={virtualizer.measureElement}
                        style={{
                          minHeight: getWorkspaceAgentMyTasksItemRowMinHeight(row),
                          marginBottom: hasRowGap ? WORKSPACE_AGENT_MY_TASKS_ROW_GAP : undefined,
                        }}
                      >
                        {renderVirtualRow(
                          row,
                          renderSessionRow,
                          onboardingReviewTask?.render,
                          toggleArchivedSection,
                          toggleAutomationsSection,
                          renderAutomationRow,
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          ) : null}
        </div>

        <WorkspaceAgentPanelMyTasksFooter isDisabled={isStartNewTaskDisabled} onStartNewTask={onStartNewTask} />
      </div>
    );
  },
);

WorkspaceAgentPanelMyTasksList.displayName = 'WorkspaceAgentPanelMyTasksList';
